import React, { useEffect, useState } from "react";
import { apiCall } from "utils/httpClient";
import { successToast, errorToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { handleDetailResponse } from "utils/utils";

// Modal Component for Allocating Visitor/Lead to Sales Managers
const AllocateSM = ({
  show,
  propertyId,
  leadId,
  handleClose,
  onAllocateSuccess,
}) => {
  const [smList, setSmList] = useState([]); // All available SMs
  const [selectedSM, setSelectedSM] = useState(null); // Selected SM
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filtered list based on search query
  const filteredSmList = smList.filter(
    (sm) =>
      sm?.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sm?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sm?.mobile?.includes(searchQuery),
  );

  // Fetch SMs for the property
  useEffect(() => {
    const fetchSMs = async () => {
      if (show && propertyId) {
        setLoading(true);
        try {
          const response = await apiCall(
            "post",
            apiEndPoints.GET_PROPERTY_BASED_SM,
            {
              property_id: propertyId,
            },
          );

          if (response?.data?.status === 200) {
            setSmList(response.data.data || []);
          } else {
            setSmList([]);
            if (response?.data?.status !== 201) {
              errorToast(
                response?.data?.message || "Failed to fetch Sales Managers",
              );
            }
          }
        } catch (error) {
          console.error("Error fetching SMs:", error);
          errorToast("Failed to fetch Sales Managers");
          setSmList([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchSMs();
  }, [show, propertyId]);

  // Handle SM selection (radio button - only one can be selected)
  const handleSMSelection = (sm) => {
    setSelectedSM(sm);
  };

  // Handle "Allocate" click
  const handleAllocate = async () => {
    if (!selectedSM) {
      errorToast("Please select a Sourcing Manager");
      return;
    }

    if (!leadId) {
      errorToast("Lead ID is missing");
      return;
    }

    setLoading(true);
    try {
      // First, fetch visitor details to get required fields
      const visitorDetailResponse = await apiCall(
        "post",
        apiEndPoints.VISITORDETAIL,
        {
          lead_id: leadId,
        },
      );

      if (visitorDetailResponse?.data?.status !== 200) {
        errorToast("Failed to fetch visitor details");
        setLoading(false);
        return;
      }

      const visitorData = handleDetailResponse(
        visitorDetailResponse?.data?.data || {},
      );

      // Extract customer data from nested structure (customer_detail contains customer info)
      const customerData = visitorData?.customer_detail || {};
      const first_name =
        customerData?.first_name || visitorData?.first_name || "";
      const last_name = customerData?.last_name || visitorData?.last_name || "";
      const mobile = customerData?.mobile || visitorData?.mobile || "";
      const email = customerData?.email || visitorData?.email || "";
      const property_id = visitorData?.property_id || propertyId || "";

      // Validate required fields
      if (!first_name || !mobile || !leadId) {
        console.error("Missing required fields:", {
          first_name,
          mobile,
          leadId,
          visitorData,
        });
        errorToast("Visitor data is incomplete. Please try again.");
        setLoading(false);
        return;
      }

      // Validate that lead exists and has required structure
      if (!visitorData || !visitorData._id) {
        console.error("Invalid visitor data structure:", visitorData);
        errorToast("Invalid visitor data. Please try again.");
        setLoading(false);
        return;
      }

      // Validate that lead has create_by field (required by backend)
      if (!visitorData.create_by) {
        console.error("Lead missing create_by field:", visitorData);
        errorToast("Lead data is incomplete. Cannot allocate.");
        setLoading(false);
        return;
      }

      // Update the visitor/lead with for_sm_id using editVisitor API
      // Backend editVisit function only REQUIRES: lead_id, first_name, mobile (line 2261)
      // We only need to send the minimum required fields for SM allocation
      // The visitor already exists, so we don't need to send all its data again
      const formData = new FormData();
      formData.append("lead_id", leadId);
      formData.append("for_sm_id", selectedSM._id);
      formData.append("first_name", first_name);
      formData.append("mobile", mobile);

      // property_id might be needed for SM allocation logic in backend
      if (property_id) {
        formData.append("property_id", property_id);
      }

      // Debug: Log what we're sending
      console.log("🔵 AllocateSM - Sending minimal required fields:", {
        lead_id: leadId,
        for_sm_id: selectedSM._id,
        first_name,
        mobile,
        property_id: property_id || "not sent",
      });

      // Convert FormData to plain object, then to URLSearchParams string
      // Backend expects application/x-www-form-urlencoded format (express.urlencoded middleware)
      // Issue: FormData object is not being parsed correctly by backend
      // Solution: Convert to URLSearchParams string
      const formDataObj = {};
      for (const [key, value] of formData.entries()) {
        formDataObj[key] = value;
      }

      // Convert to URLSearchParams string for proper encoding
      const urlParams = new URLSearchParams(formDataObj).toString();

      console.log("🔵 AllocateSM - Prepared data for backend:", {
        endpoint: apiEndPoints.EDITVISITOR,
        method: "POST",
        urlParamsString: urlParams,
        formDataObject: formDataObj,
        lead_id: formDataObj.lead_id,
        for_sm_id: formDataObj.for_sm_id,
        first_name: formDataObj.first_name,
        mobile: formDataObj.mobile,
        property_id: formDataObj.property_id,
        selectedSM: {
          _id: selectedSM._id,
          user_name: selectedSM.user_name,
        },
        leadId,
        propertyId,
      });

      // Send URLSearchParams string to backend
      // Backend expects application/x-www-form-urlencoded (express.urlencoded middleware)
      // Converting FormData to URLSearchParams string ensures proper parsing
      console.log("🔵 AllocateSM - Sending to backend:", {
        endpoint: apiEndPoints.EDITVISITOR,
        urlParamsString: urlParams,
        keyFields: {
          lead_id: formDataObj.lead_id,
          for_sm_id: formDataObj.for_sm_id,
          first_name: formDataObj.first_name,
          mobile: formDataObj.mobile,
          property_id: formDataObj.property_id,
        },
      });

      const response = await apiCall(
        "post",
        apiEndPoints.EDITVISITOR,
        urlParams,
        {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      );

      if (response?.data?.status === 200) {
        successToast(
          response?.data?.message || "Visitor allocated successfully",
        );
        if (onAllocateSuccess) {
          onAllocateSuccess(selectedSM);
        }
        handleClose();
      } else {
        console.error("🔵 AllocateSM - Backend error:", response?.data);
        errorToast(response?.data?.message || "Failed to allocate visitor");
      }
    } catch (error) {
      console.error("Error allocating visitor:", error);
      errorToast(
        "Failed to allocate visitor. Please check console for details.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Return null if modal is not shown
  if (!show) return null;

  return (
    <>
      {/* Backdrop with blur effect */}
      <div
        className="modal-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(5px)",
          zIndex: 1040,
        }}
      ></div>

      {/* Modal Dialog */}
      <div
        className="modal show"
        style={{ display: "block", zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content" style={{ height: "80vh" }}>
            <div className="modal-header">
              <h5 className="modal-title">
                Allocate Visitor to Sourcing Manager
              </h5>
              <button type="button" className="close" onClick={handleClose}>
                <span>&times;</span>
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Display selected SM */}
              {selectedSM && (
                <div
                  style={{
                    border: "1px solid #007bff",
                    padding: "10px",
                    marginBottom: "20px",
                    backgroundColor: "#e7f1ff",
                    borderRadius: "4px",
                  }}
                >
                  <strong>Selected Sourcing Manager:</strong>
                  <div style={{ marginTop: "5px" }}>
                    <div>
                      <strong>Name:</strong> {selectedSM.user_name}
                    </div>
                    <div>
                      <strong>Email:</strong> {selectedSM.email}
                    </div>
                  </div>
                </div>
              )}

              {/* Search input */}
              <div style={{ marginBottom: "20px" }}>
                <input
                  type="text"
                  placeholder="Search by Name, Email or Mobile"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                  }}
                />
              </div>

              {/* Loading state */}
              {loading && smList.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  Loading Sourcing Managers...
                </div>
              )}

              {/* No SMs found */}
              {!loading && smList.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                  }}
                >
                  No Sourcing Managers found for this property.
                </div>
              )}

              {/* List of SMs with radio buttons */}
              {!loading && smList.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {filteredSmList.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No Sales Managers match your search.
                    </div>
                  ) : (
                    filteredSmList.map((sm) => (
                      <label
                        key={sm._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px",
                          border:
                            selectedSM?._id === sm._id
                              ? "2px solid #007bff"
                              : "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: "pointer",
                          backgroundColor:
                            selectedSM?._id === sm._id ? "#f0f8ff" : "#fff",
                        }}
                      >
                        <input
                          type="radio"
                          name="selectedSM"
                          checked={selectedSM?._id === sm._id}
                          onChange={() => handleSMSelection(sm)}
                          style={{ marginRight: "10px" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold" }}>
                            {sm.user_name}
                          </div>
                          <div style={{ fontSize: "0.9em", color: "#666" }}>
                            {sm.email} | {sm.mobile}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAllocate}
                disabled={loading || !selectedSM}
              >
                {loading ? "Allocating..." : "Allocate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllocateSM;
